import * as Commons from "oc-commons/Commons";
import { AbstractProperty } from "./properties/AbstractProperty";
import { json, KeyValue } from "../global";
import { PNodeListMeta } from "./properties/PNodeListMeta";
import { v2 } from "oc-threejs/short3";

let uidCounter = 1;

export type PropertyOrKeyValue = AbstractProperty<any, any> | KeyValue;

export type NodeConstructor = new (...args: any[]) => Node;

/**
 * @abstract
 */
export class Node implements KeyValue {
  id: number;
  uid = uidCounter++;
  _dataOld = {};
  _dataChanged = {};
  _properties: PropertyOrKeyValue[];
  _propertiesMap: { [key: string]: AbstractProperty<any, any> } = null;

  constructor() {
    this._dataOld = {};
    this._dataChanged = {};
  }

  init(): this {
    return this;
  }

  onShow() {}

  onRemove() {}

  getProperties() {
    if (this._properties == null) {
      this._resetProperties();
    }
    return this._properties;
  }

  _resetProperties() {
    this._propertiesMap = {};
    this._properties = this.createProperties();
    this._properties
      .filter((p) => p instanceof AbstractProperty)
      .forEach((p) => (this._propertiesMap[p.name] = p as AbstractProperty<any, any>));
  }

  createProperties(): PropertyOrKeyValue[] {
    return [];
  }

  /**
   * Sinn dieser Methode ist es, in den Typen die Materialien, Geometrien etc. nur bei Bedarf zu ändern.
   * Gibt zurück, ob ein Wert aus data sich geändert hat im Vergleich zum letztmaligen updateScene-Aufruf
   */
  changed(path: string, accessor = (data) => (data ? data[path] : undefined)): boolean {
    const buffered = this._dataChanged[path];
    if (buffered !== undefined) {
      return buffered;
    }
    const valueNew = accessor(this);
    const valueOld = this._dataOld[path];
    const changed = (this._dataChanged[path] = !Commons.isEqualEpsilon(valueOld, valueNew));
    if (changed) {
      this._dataOld[path] = Commons.cloneDeep(valueNew);
    }
    return changed;
  }

  fillWithDefaults() {
    this.fromJSON({});
    return this;
  }

  fromJSON(json: json = {}, filter = (p) => p.type && !(p instanceof PNodeListMeta)) {
    const props = this.getProperties().filter(filter);
    props.forEach((p) => p.fromJSON(json));
    return this;
  }

  toJSON(filter = (p) => p.type && !(p instanceof PNodeListMeta)) {
    const json = {};
    const properties = this.getProperties();
    const saveIt = (p) => p.defaultInJson || !p.isEqual(p.get(), p.default);
    const filtered = properties.filter((p) => filter(p) && saveIt(p));
    filtered.forEach((p) => (json[p.name] = p.toJSON()));
    return json;
  }

  getKeyProperty(names = ["key", "name"]) {
    const ps = this.getProperties();
    const primary = ps.find((p) => p["primaryKey"]);
    if (primary) {
      return primary;
    }
    for (const key of names) {
      const prop = ps.find((p) => p.name && key.toLowerCase() == p.name.toLowerCase());
      if (prop) {
        return prop;
      }
    }
    return null;
  }

  getKey(names = ["key", "name"]) {
    const prop = this.getKeyProperty(names);
    const name = prop?.get();
    if (!Commons.isEmpty(name)) {
      return name;
    }
    return null;
  }

  getName(): string {
    const key = this.getKey(["name", "key", "type"]);
    if (key == null) {
      return "NO NAME OR TYPE PROPERTY";
    }
    return key;
  }

  findProperty(name: string): AbstractProperty<any, any> {
    this.getProperties();
    return this._propertiesMap[name];
  }

  orderAndGroup(propertyGruppierung: PropertyOrKeyValue[], properties: PropertyOrKeyValue[]) {
    const map = new Map();
    properties.filter((p) => p.name).forEach((p) => map.set(p.name, p));
    const props = propertyGruppierung
      .map((x) => {
        if (x["group"] || x["separator"] || x["render"] || x["unused"]) {
          return x;
        }
        const property = map.get(x["name"]);
        if (property) {
          Object.assign(property, x);
          return property;
        }
        return null;
      })
      .filter((x) => x != null);
    const used = new Set(props);
    const unused = properties.filter((p) => !used.has(p));
    if (props.find((x) => x["unused"])) {
      return props.flatMap((p) => (p["unused"] ? unused : [p]));
    } else {
      return props.concat(unused);
    }
  }
}
